import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import { IRoleWithPermissions } from "../../../@types/admin/AdminAccess";
import { TableSorted } from "../../../components/TableSorted";
import {
  useGrantRolePermission,
  useRevokeRolePermission,
} from "../../../hooks/mutations/admin/access";
import {
  useRolePermissionCatalog,
  useRolesWithPermissions,
} from "../../../hooks/queries/admin/access";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  ErrorMessage,
  ModalContainer,
  ModalForm,
  ModalTitle,
  PageContainer,
  PageHeader,
  PageTitle,
  TableContainer,
  TableLoadingState,
  TableWrapper,
} from "../shared";
import { RoleCodeCell, SectionLabel } from "./Access.styles";
import { PermissionChecklist } from "./PermissionChecklist";

interface IManageRolePermissionsModalProps {
  role: IRoleWithPermissions;
}

/**
 * Grant/revoke permissions on a role — toggles apply immediately (EN-5129).
 * Local granted-set state keeps the checkboxes responsive; failures revert
 * with a toast.
 */
function ManageRolePermissionsModal({
  role,
}: IManageRolePermissionsModalProps) {
  const { showToast } = useToast();
  const { permissionCatalog, isLoading } = useRolePermissionCatalog();
  const { grantRolePermission } = useGrantRolePermission();
  const { revokeRolePermission } = useRevokeRolePermission();

  const [granted, setGranted] = useState<Set<string>>(
    () => new Set(role.permissionCodes)
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleToggle = async (permissionCode: string, grant: boolean) => {
    setIsSaving(true);
    setGranted((prev) => {
      const next = new Set(prev);
      if (grant) next.add(permissionCode);
      else next.delete(permissionCode);
      return next;
    });

    try {
      if (grant) {
        await grantRolePermission({ roleCode: role.code, permissionCode });
      } else {
        await revokeRolePermission({ roleCode: role.code, permissionCode });
      }
    } catch (error) {
      setGranted((prev) => {
        const next = new Set(prev);
        if (grant) next.delete(permissionCode);
        else next.add(permissionCode);
        return next;
      });
      showToast(
        errorToastMessage(
          error,
          `Failed to ${grant ? "grant" : "revoke"} ${permissionCode}`
        ),
        "warning"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{role.displayName}</ModalTitle>
      <ModalForm>
        <SectionLabel>
          Permissions granted to every user with this role
        </SectionLabel>
        {isLoading ? (
          <ErrorMessage>Loading permission catalog...</ErrorMessage>
        ) : (
          <PermissionChecklist
            catalog={permissionCatalog}
            granted={granted}
            disabled={isSaving}
            onToggle={handleToggle}
          />
        )}
      </ModalForm>
    </ModalContainer>
  );
}

/**
 * Role Permissions admin page (EN-5129, portal-only — never existed in
 * Retool): which permissions each role in the catalog grants.
 */
export function AdminRolePermissions() {
  document.title = "Role permissions - Admin";

  const { openModal } = useModal();
  const { roles, isLoading, isError } = useRolesWithPermissions();

  const handleManageClick = (role: IRoleWithPermissions) => {
    openModal(<ManageRolePermissionsModal role={role} />, {
      // Wide enough for the checklist's two columns; it drops to one below
      // 720px (see ChecklistGrid).
      width: "min(820px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IRoleWithPermissions, string>[] = useMemo(
    () => [
      { accessorKey: "displayName", header: "Role", enableSorting: true },
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => <RoleCodeCell>{row.original.code}</RoleCodeCell>,
        enableSorting: true,
      },
      {
        id: "permissions",
        header: "Permissions",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.permissionCodes.length > 0 ? (
            <span>{row.original.permissionCodes.join(", ")}</span>
          ) : (
            <span>—</span>
          ),
      },
    ],
    [roles]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Role permissions</PageTitle>
      </PageHeader>

      {isError && (
        <ErrorMessage>
          Failed to load roles. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-role-permissions-roles"]]}>
          <TableSorted
            columns={columns}
            data={roles}
            onRowClick={handleManageClick}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No roles found"
            }
          />
        </TableWrapper>
      </TableContainer>
    </PageContainer>
  );
}
